import { DatalayerService } from '@analytics/datalayer';
import { inject, Injectable } from '@angular/core';
import { WINDOW } from '@innogy/utils-dom';

import type { ABTestingVariant } from '../ab-testing.model';
import type { AlloyProposition } from './adobe-web-sdk.model';
import { AdobeWebSDKService } from './adobe-web-sdk.service';

const COMPONENT_TEST_EVENT_NAME = 'ab-experience-assigned';
const COMPONENT_TEST_STORAGE_KEY = 'ab-experience';

@Injectable({
  providedIn: 'root',
})
export class ABTestingService {
  readonly #datalayer = inject(DatalayerService);
  readonly #adobeWebSDK = inject(AdobeWebSDKService);
  readonly #window = inject(WINDOW);

  private readonly errorPrefix = `[AB testing]`;

  /**
   * fetches information based on the test we're running and returns the assigned variant.
   * @param testName name of the A/B test we want to get an assignment for. This value has to correspond with the mbox name configured in instance of Adobe Target serving out JSON Offers.
   * @param defaultVariant variant to return in case of no test data being found, functional errors or any other unexpected problems.
   * @param permittedVariants list of variants that are allowed to be returned from Adobe Web SDK. When the returned variant is not in this list we default out.
   * @returns test variant returned from Adobe Web SDK.
   */
  public async getAssignedVariant(
    testName: string,
    defaultVariant: ABTestingVariant,
    permittedVariants: ABTestingVariant[]
  ) {
    // Default out to default variant when the web SDK is disabled.
    if (!this.#adobeWebSDK.isEnabled()) {
      return {
        variant: defaultVariant,
        assignmentResolved: false,
        trackingPayload: [],
      };
    }

    // Make a request to Adobe Web SDK to get offers
    try {
      const { propositions } =
        await this.#adobeWebSDK.fetchPropositionsForTest(testName);

      // Extract the proposition
      const proposition = propositions.find((p) => p.scope === testName);
      const propositionContent = proposition?.items?.[0]?.data?.content;

      // Handle cases where no proposition is found.
      if (!propositionContent) {
        return {
          variant: defaultVariant,
          assignmentResolved: false,
          trackingPayload: [],
        };
      }

      // Handle misconfigured AB tests
      if (
        !(propositionContent instanceof Object) ||
        !propositionContent['variant']
      ) {
        console.warn(
          `${this.errorPrefix} Test is misconfigured, the configured offer is not a JSON offer containing a 'variant' key. Falling back to the default variant '${defaultVariant}.'`
        );
        return {
          variant: defaultVariant,
          assignmentResolved: false,
          trackingPayload: [],
        };
      }

      const { variant } = propositionContent;

      // Check if the variant is in the list of permitted variants
      if (!permittedVariants.includes(variant)) {
        console.warn(
          `${this.errorPrefix} Test is misconfigured, the configured variant '${variant}' is not present in the list of permitted variants '${permittedVariants}. Falling back to the default variant '${defaultVariant}.`
        );
        return {
          variant: defaultVariant,
          assignmentResolved: false,
          trackingPayload: [],
        };
      }
      // Return the assigned variant
      return {
        variant: variant as ABTestingVariant,
        assignmentResolved: true,
        trackingPayload: propositions,
      };
    } catch (error) {
      console.error(
        `${this.errorPrefix} An error occurred while fetching the assigned variant:`,
        error
      );
      return {
        variant: defaultVariant,
        assignmentResolved: false,
        trackingPayload: [],
      };
    }
  }

  /**
   * sends a signal to the Adobe web SDK that the assigned items have been displayed.
   * Also sends an event to the GDDL marking that a test has been assigned and stores the test in session storage.
   * @param payload a list of propositions that have been displayed.
   */
  public async trackAssignment(payload: AlloyProposition[]) {
    await this.#adobeWebSDK.applyPropositions({
      propositions: payload,
    });
    this.#adobeWebSDK.markPropositionAsDisplayed(payload);
    this.sendTestAssignedEvent(payload);
    this.#storeAssignedTestInSessionStorage(payload);
    this.#pushTestsToDatalayer(payload);
  }

  /**
   * Dispatches an event signalling that the user is assigned to an A/B test experience.
   */
  public sendTestAssignedEvent(payload: AlloyProposition[]) {
    this.#datalayer.pushEvent({
      event: COMPONENT_TEST_EVENT_NAME,
      info: payload.map((proposition) => ({
        test: proposition.scope,
        activity: proposition.scopeDetails.activity['id'],
        experience: proposition.scopeDetails.experience['id'],
        offerPayload: proposition.items?.[0]?.data?.content,
      })),
    });
  }

  #pushTestsToDatalayer(payload: AlloyProposition[]) {
    const tests = payload.map((p) => ({
      scope: p.scope,
      variant:
        (p.items[0]?.data.content as { variant: string } | undefined)
          ?.variant ?? 'A',
    }));

    this.#datalayer.pushVariable('activeABTests', tests, {
      merge: true,
    });
  }

  /**
   * stores the **first** test a user is assigned to in the session storage so external tools (read: Adobe Launch). Can access this data and use it in outward events.
   * @NOTE this means we can only store ONE test assignment at a time - TO BE DEPRECATED!
   * @param payload list of propositions containing a scope and variant data.
   */
  #storeAssignedTestInSessionStorage(payload: AlloyProposition[]) {
    const firstTest = payload[0];
    const variant = (
      firstTest?.items[0]?.data.content as { variant: string } | undefined
    )?.variant;
    const testName = `${firstTest?.scope}:${variant}`;

    this.#window?.sessionStorage?.setItem(COMPONENT_TEST_STORAGE_KEY, testName);
  }
}
